import { observable, computed, action, reaction } from 'mobx';
import { WebGivingPage } from '../index';
import { WebGivingMerchant, PaymentMethodType, WebGivingPaymentResultStatus, WebGivingPaymentMethodModel } from '../../webgiving-generated';
import { IWebGivingPaymentState, IWebGivingRecurringPaymentInfo, GiftType, generateOneTimeGiftDateLabel as generateOneTimeGiftDateLabelNew } from '../../webgiving-payment-state';
import { FormField } from '../../components/form-field';
import { ValidationState, validateFields } from '../../validation/view-model-validator';
import {
	RuleType,
	createValidatorFunc,
	fieldValidationSuccess,
	FieldValidationFailure,
} from '../../validation/validation-rules';
import { WebGivingErrorType } from '../../components/error-message/error-message';
import { getSelectedFundName, getSelectedFundKey } from '../../utils/reference-field-helper';
import { WebGivingPageViewModelBase } from '../page-view-model-base';
import { getCalculatedAmountWithFees, getIndividuallyCalculatedAmountWithFees } from '../../utils/helper-utils';
import { getOverMaxLimitError } from '../../utils/error-utils';

const blockingPaymentFailures = [
	WebGivingPaymentResultStatus.InvalidAmount,
	WebGivingPaymentResultStatus.MaximumYearExceeded,
	WebGivingPaymentResultStatus.CardFail,
	WebGivingPaymentResultStatus.PaymentMethodNotSupported,
];
const failuresResolvedByNewPaymentMethod = [
	WebGivingPaymentResultStatus.InvalidAmount,
	WebGivingPaymentResultStatus.CardFail,
	WebGivingPaymentResultStatus.PaymentMethodNotSupported,
];

export const enum ConfirmingType {
	NONE,
	GIVING,
	APPLEPAY
}
export class PageConfirmGiftViewModel extends WebGivingPageViewModelBase {
	readonly type: WebGivingPage.ConfirmGift = WebGivingPage.ConfirmGift;

	amount: number;

	@observable
	validationState?: ValidationState;

	@observable
	payerElectedToCoverFees: FormField<boolean>;

	@observable
	payerEnabledAnniversaryReminder = FormField.Unvalidated(false);

	@observable
	authorizeDebitAccount: FormField<boolean>;

	@observable
	paymentMethodId: FormField<string>;

	@computed
	get paymentBlocked(): boolean {
		return this.isBlocked || (this.error && this.error.errorType === WebGivingErrorType.PaymentError ? blockingPaymentFailures.indexOf(this.error.errorReason) !== -1 : false);
	}

	@computed
	get calculatedAmountWithFees() {
		return getCalculatedAmountWithFees(this.paymentState.LineItems, this.merchantInfo, this.payerElectedToCoverFees.value, this.amount);
	}

	@computed
	get individuallyCalculatedAmountWithFees() {
		return getIndividuallyCalculatedAmountWithFees(this.paymentState.LineItems, this.merchantInfo, this.payerElectedToCoverFees.value);
	}

	@computed
	get merchantSupportsCard(): boolean {
		return this.merchantInfo.SupportedCardBrandKeys && this.merchantInfo.SupportedCardBrandKeys.length > 0;
	}

	@observable
	merchantSupportsACH: boolean;

	recurringInfo: IWebGivingRecurringPaymentInfo | null = null;

	percentageOfPaymentAddedToCoverFees: number;

	fundName: string;

	fundKey: string;

	@computed
	get paymentMethod() {
		if (!this.paymentMethodId.value) {
			return this.paymentMethods.filter(x => x.IsPreferred)[0];
		}
		const [paymentMethod] = this.paymentMethods.filter(x => x.Id === this.paymentMethodId.value);
		return paymentMethod;
	}

	@observable
	merchantSupportsPayerCoversFees: boolean;

	@observable
	responseTimeNotice: boolean;

	@observable
	confirmingType: ConfirmingType = ConfirmingType.NONE;

	@observable
	applePayAvailable: boolean;

	oneTimeGiftDateLabel: string;

	termsAndConditionsUrl?: string;

	privacyStatementUrl?: string;

	constructor(
		private paymentState: IWebGivingPaymentState,
		public paymentMethods: WebGivingPaymentMethodModel[],
		public merchantInfo: WebGivingMerchant,
		public showTermsAndConditions: boolean,
		public paymentDetailsText: string,
		public hideACH = false,
		public skippedGivePage = false,
		applePayAvailable = false,
		termsAndConditionsUrl?: string,
		privacyStatementUrl?: string
	) {
		super();
		const [paymentMethod] = this.paymentMethods.filter(x => x.Id === paymentState.paymentMethodId);
		this.paymentMethodId = FormField.Unvalidated((paymentMethod && paymentMethod.Id) || this.getPreferredPaymentMethod());
		this.payerElectedToCoverFees = FormField.Unvalidated(paymentState.payerElectedToCoverFees);
		this.authorizeDebitAccount = new FormField<boolean>(false, createValidatorFunc((value: boolean) => {
			if (this.paymentMethod.PaymentMethodType !== PaymentMethodType.ACH) {
				return fieldValidationSuccess;
			}
			if (value) {
				return fieldValidationSuccess;
			}
			return new FieldValidationFailure(RuleType.Custom, 'Please select this option to authorize this payment method.');
		}, RuleType.Custom));
		this.amount = this.paymentState.amount;

		if (paymentState.giftType === GiftType.Recurring) {
			this.recurringInfo = paymentState.recurringInfo;
		}

		this.applePayAvailable = applePayAvailable;
		this.fundName = getSelectedFundName(paymentState.referenceFieldValues, merchantInfo.CustomFields);
		this.fundKey = getSelectedFundKey(paymentState.referenceFieldValues, merchantInfo.CustomFields);
		this.merchantSupportsACH = this.merchantInfo.SupportsAch;
		this.merchantSupportsPayerCoversFees = this.merchantInfo.SupportsPayerCoversFees;
		this.oneTimeGiftDateLabel = generateOneTimeGiftDateLabelNew(paymentState);
		this.termsAndConditionsUrl = termsAndConditionsUrl;
		this.privacyStatementUrl = privacyStatementUrl;

		if (this.applePayAvailable) {
			reaction(
				() => this.isLoading,
				(val) => {
					if (!val && this.confirmingType !== ConfirmingType.NONE && this.applePayAvailable) {
						this.confirmingType = ConfirmingType.NONE;
					}
				}
			);
		}
		this.handleMaxLimitAfterProcessingCosts();
		this.payerElectedToCoverFees.updateSubject.register(() => this.handleMaxLimitAfterProcessingCosts());
	}

	validate(): ValidationState {
		return validateFields(this);
	}

	@action.bound
	handleMaxLimitAfterProcessingCosts() {
		// TODO (No ticket yet) Message translation params is tech debt
		this.error = getOverMaxLimitError(this.paymentState.LineItems, this.payerElectedToCoverFees.value, this.merchantInfo, this.amount);
	}

	@action.bound
	handlePaymentMethodChange() {
		if (this.error && this.error.errorType === WebGivingErrorType.PaymentError && failuresResolvedByNewPaymentMethod.indexOf(this.error.errorReason) !== -1) {
			this.error = undefined;
		}
		this.handleMaxLimitAfterProcessingCosts();
	}

	@action.bound
	setConfirmingType(type: ConfirmingType) {
		this.confirmingType = type;
	}

	@action.bound
	setApplePayAvailability(isAvailable: boolean) {
		this.applePayAvailable = isAvailable;

		if (!this.isLoading && this.confirmingType !== ConfirmingType.NONE && this.applePayAvailable) {
			this.confirmingType = ConfirmingType.NONE;
		}
	}

	@action.bound
	setResponseTimeNotice(base: boolean) {
		this.responseTimeNotice = base;
	}

	private getPreferredPaymentMethod() {
		let [preferredPaymentMethod] = this.paymentMethods.filter(x => x.IsPreferred);
		if (!preferredPaymentMethod) {
			preferredPaymentMethod = this.paymentMethods[0];
		}

		return preferredPaymentMethod.Id;
	}
}
